import { useCallback, useMemo, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  AlertCircle,
  ArrowLeft,
  Check,
  Clock3,
  Download,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'

import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TimelineList } from '@/components/timeline'
import { ActionPopover } from '@/components/action-popover'
import { LoadingButton } from '@/components/loading-button'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { POLineItemProductCell } from '@/components/po-line-item-product-cell'
import {
  ProductSearchPanel,
  type PickedProduct,
} from '@/components/products/product-search-panel'
import {
  useAddDropshipQuoteDraftLineItem,
  useConfirmDropshipQuoteDraft,
  useDeleteDropshipQuoteDraftLineItem,
  useDropshipQuoteDraft,
  useDropshipQuoteDraftEvents,
  usePatchDropshipQuoteDraft,
} from '@/api/hooks'
import { MentionInput, MentionText, type MentionInputHandle } from '@/components/comments'
import { useAuth } from '@/hooks/use-auth'
import type { components } from '@/api/schema'
import {
  asRecord,
  draftTotals,
  draftValidationDetails,
  fieldValue,
  friendlyValue,
  getDropshipErrorMessage,
  lineFieldValue,
  lineSellTotal,
  quoteLineItems,
  reviewLines,
  type JsonRecord,
} from '@/lib/dropship-draft-display'
import { cn, formatMoney, formatStatus, parseUTCDate } from '@/lib/utils'

type DropshipDraft = components['schemas']['DropshipQuoteDraftRead']
type DropshipDraftEvent = components['schemas']['DropshipQuoteDraftEventRead']
type DropshipPatch = components['schemas']['DropshipQuoteDraftPatch']
type DraftFieldUpdate = components['schemas']['DropshipQuoteDraftFieldUpdate']
type LineLink = components['schemas']['DropshipQuoteDraftLineLinkRead']
type FieldKind = 'text' | 'money' | 'number' | 'boolean' | 'textarea'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'
const DRAFT_TERMINAL_STATUSES = new Set(['cancelled', 'confirmed', 'project_started'])

const BILL_TO_FIELDS = [
  { key: 'bill_to.name', label: 'Name' },
  { key: 'contact.name', label: 'Contact' },
  { key: 'contact.email', label: 'Email' },
  { key: 'contact.phone', label: 'Phone' },
  { key: 'bill_to.address1', label: 'Address 1' },
  { key: 'bill_to.address2', label: 'Address 2' },
  { key: 'bill_to.city', label: 'City' },
  { key: 'bill_to.state', label: 'State' },
  { key: 'bill_to.zip', label: 'ZIP' },
]

const SHIP_TO_FIELDS = [
  { key: 'ship_to.name', label: 'Name' },
  { key: 'ship_to.contact', label: 'Contact' },
  { key: 'ship_to.email', label: 'Email' },
  { key: 'ship_to.phone', label: 'Phone' },
  { key: 'ship_to.address1', label: 'Address 1' },
  { key: 'ship_to.address2', label: 'Address 2' },
  { key: 'ship_to.city', label: 'City' },
  { key: 'ship_to.state', label: 'State' },
  { key: 'ship_to.zip', label: 'ZIP' },
]

const ACCESSORIAL_FIELDS = [
  {
    key: 'accessorials.liftgate_delivery',
    label: 'Liftgate delivery',
    kind: 'boolean' as const,
  },
  {
    key: 'accessorials.inside_delivery',
    label: 'Inside delivery',
    kind: 'boolean' as const,
  },
  {
    key: 'accessorials.residential_delivery',
    label: 'Residential delivery',
    kind: 'boolean' as const,
  },
]

const EDITABLE_LINE_FIELDS = [
  { key: 'quantity', label: 'Qty', kind: 'number' as const },
  { key: 'unit_price', label: 'List', kind: 'money' as const },
  { key: 'discount_chain', label: 'Discount' },
  { key: 'net_price', label: 'Net', kind: 'money' as const },
  { key: 'sell_price', label: 'Sell', kind: 'money' as const },
]

function friendlySpecifications(value: unknown): string | null {
  const direct = friendlyValue(value)
  if (direct) return direct
  if (Array.isArray(value)) {
    const parts = value.map(friendlyValue).filter((part): part is string => !!part)
    return parts.length ? parts.join(' · ') : null
  }
  if (value && typeof value === 'object') {
    const parts = Object.entries(value as JsonRecord)
      .map(([key, rawValue]) => {
        const renderedValue = friendlyValue(rawValue)
        return renderedValue ? `${key}: ${renderedValue}` : null
      })
      .filter((part): part is string => !!part)
    return parts.length ? parts.join(' · ') : null
  }
  return null
}

function booleanText(value: unknown): string | undefined {
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  return undefined
}

function moneyText(value: unknown): string {
  if (value == null || value === '') return '-'
  return formatMoney(String(value))
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-'
  return parseUTCDate(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function statusTone(status: string | null | undefined): string {
  switch (status) {
    case 'validated':
    case 'reviewed':
    case 'draft':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700'
    case 'needs_review':
      return 'border-amber-200 bg-amber-50 text-amber-700'
    case 'confirm_blocked':
      return 'border-blue-200 bg-blue-50 text-blue-700'
    case 'confirmed':
    case 'project_started':
      return 'border-violet-200 bg-violet-50 text-violet-700'
    case 'cancelled':
      return 'border-muted bg-muted text-muted-foreground'
    default:
      return 'border-muted bg-muted text-muted-foreground'
  }
}

function parseFieldValue(fieldKey: string, rawValue: string, kind?: FieldKind): unknown {
  if (kind === 'boolean') {
    if (rawValue === 'true') return true
    if (rawValue === 'false') return false
    return null
  }
  const trimmed = rawValue.trim()
  if ((kind === 'number' || kind === 'money' || /\.quantity$/.test(fieldKey)) && trimmed) {
    const numberValue = Number(trimmed)
    return Number.isFinite(numberValue) ? numberValue : rawValue
  }
  return rawValue
}

function confirmationProject(draft: DropshipDraft) {
  const result = asRecord(draft.confirmation_result)
  const project = asRecord(result.project)
  const links = asRecord(result.links)
  return {
    uuid: friendlyValue(project.uuid),
    id: friendlyValue(result.project_id ?? project.id),
    href: friendlyValue(links.dashboard_project),
  }
}

function draftReference(draft: DropshipDraft) {
  return (
    friendlyValue(fieldValue(draft, 'quote.job_reference_number')) ||
    friendlyValue(fieldValue(draft, 'quote.number')) ||
    friendlyValue(fieldValue(draft, 'quote.project_name')) ||
    draft.source_file_name.replace(/\.aqproj$/i, '').trim() ||
    'Untitled dropship project'
  )
}

function FieldEditor({
  label,
  fieldKey,
  value,
  kind = 'text',
  disabled,
  compact,
  align = 'left',
  displayValue,
  showLabel = true,
  onSave,
}: {
  label: string
  fieldKey: string
  value: unknown
  kind?: FieldKind
  disabled?: boolean
  compact?: boolean
  align?: 'left' | 'right'
  displayValue?: string
  showLabel?: boolean
  onSave: (fieldKey: string, rawValue: string, kind?: FieldKind) => void
}) {
  const shouldKeepMoneyOnOneLine = kind === 'money'

  return (
    <div
      className={cn(
        'space-y-1',
        !compact && 'rounded-md border bg-background p-3',
        shouldKeepMoneyOnOneLine && align === 'right' && 'shrink-0',
      )}
    >
      {showLabel && <p className="text-xs font-medium text-muted-foreground">{label}</p>}
      <POInlineEditableField
        value={value}
        displayValue={displayValue ?? (kind === 'boolean' ? booleanText(value) : undefined)}
        kind={kind}
        disabled={disabled}
        align={align}
        title={`Click to edit ${label.toLowerCase()}`}
        inputClassName={cn(
          kind === 'textarea' ? 'w-full' : 'w-full',
          align === 'right' && 'text-right',
        )}
        buttonClassName={cn(
          'min-h-7 max-w-full text-left',
          shouldKeepMoneyOnOneLine ? 'whitespace-nowrap break-normal tabular-nums' : 'break-words',
          align === 'right' && 'text-right',
        )}
        onConfirm={(rawValue) => onSave(fieldKey, rawValue, kind)}
      />
    </div>
  )
}

function AddressPanel({
  title,
  subtitle,
  draft,
  fields,
  onSave,
  disabled,
}: {
  title: string
  subtitle: string
  draft: DropshipDraft
  fields: { key: string; label: string }[]
  onSave: (fieldKey: string, rawValue: string, kind?: FieldKind) => void
  disabled?: boolean
}) {
  return (
    <Card className="h-full" data-quote-header-section={title}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">
          {title}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </CardHeader>
      <CardContent className="grid gap-2">
        {fields.map((field) => (
          <FieldEditor
            key={field.key}
            label={field.label}
            fieldKey={field.key}
            value={fieldValue(draft, field.key)}
            compact
            disabled={disabled}
            onSave={onSave}
          />
        ))}
      </CardContent>
    </Card>
  )
}

function FromPanel() {
  return (
    <Card className="h-full bg-muted/20" data-quote-header-section="From">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">
          From
        </CardTitle>
        <p className="text-xs text-muted-foreground">USA-RS billing account</p>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        <p className="font-semibold">USA Restaurant Suppliers</p>
        <p>Devon Zielinski</p>
        <p>11500 S Pipeline Rd</p>
        <p>Dallas, Texas 76040</p>
        <p>888-307-5030</p>
      </CardContent>
    </Card>
  )
}

function SourceFileCard({ draft, compact = false }: { draft: DropshipDraft; compact?: boolean }) {
  const { getAccessToken } = useAuth()
  const isScratchDraft = draft.source_origin === 'scratch'
  const sourceLabel = isScratchDraft ? 'Started from scratch' : formatStatus(draft.source_origin)
  const sourceTitle = isScratchDraft ? 'Project source' : 'Uploaded source file'
  const sourceDescription = isScratchDraft
    ? 'Created manually from New Project without an uploaded source artifact.'
    : 'Original quote upload used to create this draft.'

  const handleDownload = useCallback(async () => {
    const token = await getAccessToken()
    if (!token) return
    const resp = await fetch(
      `${API_BASE_URL}/dashboard/dropship-intake/drafts/${draft.uuid}/source/download`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    )
    if (!resp.ok) {
      toast.error('Source file download failed')
      return
    }
    const blob = await resp.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = draft.source_file_name
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [draft.source_file_name, draft.uuid, getAccessToken])

  const fileContent = (
    <div className="flex items-start gap-2 rounded-lg border bg-card p-2.5 md:items-center">
      <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground md:mt-0" />
      <div className="min-w-0 flex-1">
        <p className="break-words text-sm font-medium">{sourceLabel}</p>
        {!isScratchDraft && (
          <p className="mt-0.5 break-words text-xs text-muted-foreground">{draft.source_file_name}</p>
        )}
      </div>
      {!isScratchDraft && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={handleDownload}
          title="Download"
        >
          <Download className="h-4 w-4" />
        </Button>
      )}
    </div>
  )

  if (compact) {
    return (
      <div className="w-full max-w-md space-y-2 text-left lg:text-right">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {sourceTitle}
        </p>
        {fileContent}
      </div>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{sourceTitle}</CardTitle>
        <p className="text-sm text-muted-foreground">{sourceDescription}</p>
      </CardHeader>
      <CardContent>{fileContent}</CardContent>
    </Card>
  )
}

function QuoteHeader({ draft }: { draft: DropshipDraft }) {
  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Job reference
          </p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">{draftReference(draft)}</h1>
          {friendlyValue(fieldValue(draft, 'quote.date')) && (
            <p className="mt-1 text-sm text-muted-foreground">Quote date {friendlyValue(fieldValue(draft, 'quote.date'))}</p>
          )}
        </div>
        <div className="flex min-w-[280px] flex-col items-start gap-3 lg:items-end">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={cn('text-xs', statusTone(draft.status))}>
              {formatStatus(draft.status)}
            </Badge>
            <Badge variant="secondary" className="text-xs">
              Updated {formatDateTime(draft.updated_at)}
            </Badge>
          </div>
          <SourceFileCard draft={draft} compact />
        </div>
      </div>
    </div>
  )
}

function QuotePartiesHeader({
  draft,
  onSave,
  disabled,
}: {
  draft: DropshipDraft
  onSave: (fieldKey: string, rawValue: string, kind?: FieldKind) => void
  disabled?: boolean
}) {
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <AddressPanel
        title="Bill To"
        subtitle="Editable billing account, contact, and address fields"
        draft={draft}
        fields={BILL_TO_FIELDS}
        onSave={onSave}
        disabled={disabled}
      />
      <AddressPanel
        title="Ship To"
        subtitle="Editable delivery destination"
        draft={draft}
        fields={SHIP_TO_FIELDS}
        onSave={onSave}
        disabled={disabled}
      />
      <FromPanel />
    </div>
  )
}

function ProductIdentity({ line, link }: { line: JsonRecord; link?: LineLink }) {
  const parsedSku = friendlyValue(line.sku)
  const model =
    link?.product_model ??
    link?.model ??
    friendlyValue(line.model) ??
    friendlyValue(line.mfr_model) ??
    friendlyValue(line.sku) ??
    friendlyValue(line.item_number) ??
    'Product not parsed'
  const description = firstDistinctValue(model, [
    line.description,
    line.details,
    line.product_description,
    line.title,
    line.name,
  ])
  const manufacturerName =
    link?.manufacturer_display_name ??
    link?.manufacturer_name ??
    friendlyValue(line.manufacturer_name) ??
    friendlyValue(line.manufacturer) ??
    friendlyValue(line.brand_name) ??
    friendlyValue(line.brand)
  return (
    <POLineItemProductCell
      model={model}
      description={description}
      sku={parsedSku ?? link?.product_model ?? link?.model}
      specifications={friendlySpecifications(link?.product_specifications)}
      productUuid={link?.product_uuid}
      imageUrl={
        link?.product_image_url ??
        friendlyValue(line.image_url) ??
        friendlyValue(line.product_image_url) ??
        friendlyValue(line.thumbnail_url) ??
        friendlyValue(line.image) ??
        friendlyValue(line.thumbnail)
      }
      manufacturerName={manufacturerName}
      manufacturerUuid={link?.manufacturer_uuid}
      className="min-w-0 py-1"
    />
  )
}

function firstDistinctValue(compareTo: string, values: unknown[]): string | null {
  const normalizedCompare = compareTo.trim().toLocaleLowerCase()
  for (const value of values) {
    const candidate = friendlyValue(value)
    if (!candidate) continue
    if (candidate.trim().toLocaleLowerCase() === normalizedCompare) continue
    return candidate
  }
  return null
}

function AddProductsDialog({ draft, disabled }: { draft: DropshipDraft; disabled?: boolean }) {
  const [open, setOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [selectedProduct, setSelectedProduct] = useState<PickedProduct | null>(null)
  const [quantity, setQuantity] = useState(1)
  // Products priced on each quote take their Net and Sell here.
  const [netPrice, setNetPrice] = useState('')
  const [sellPrice, setSellPrice] = useState('')
  const addLineItem = useAddDropshipQuoteDraftLineItem(draft.uuid)

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen && !addLineItem.isPending) {
      setSelectedProduct(null)
      setQuantity(1)
      setPage(1)
      addLineItem.reset()
    }
  }

  const handleSelectProduct = (product: PickedProduct) => {
    setSelectedProduct(product)
    setNetPrice('')
    setSellPrice('')
    addLineItem.reset()
  }
  const needsPrice = Boolean(selectedProduct?.pricedOnEachQuote)
  const priceEntered =
    netPrice.trim() !== '' && Number(netPrice) >= 0 && sellPrice.trim() !== '' && Number(sellPrice) > 0

  const handleAddProduct = () => {
    if (!selectedProduct) return
    addLineItem.mutate(
      {
        product_uuid: selectedProduct.uuid,
        quantity,
        manual_review: false,
        ...(needsPrice ? { net_price: netPrice, unit_price: sellPrice } : {}),
      },
      {
        onSuccess: () => {
          toast.success('Product added to quote')
          handleOpenChange(false)
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" disabled={disabled}>
          <Plus className="h-4 w-4" />
          Add products
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-[min(1200px,calc(100vw-2rem))]">
        <DialogHeader>
          <DialogTitle>Add products</DialogTitle>
          <DialogDescription>
            Search the catalog with the same filters and facets as Products, then choose a quantity
            before adding the product as a quote line item.
          </DialogDescription>
        </DialogHeader>

        {addLineItem.error && (
          <div
            role="alert"
            className="flex gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">Product could not be added.</p>
              <p>{getErrorMessage(addLineItem.error, 'Try again or choose a different product.')}</p>
            </div>
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0 rounded-lg border p-3">
            <ProductSearchPanel
              page={page}
              onPageChange={setPage}
              onSelectProduct={handleSelectProduct}
              defaultResults={
                <div className="rounded-lg border border-dashed bg-muted/20 p-6 text-center text-sm text-muted-foreground">
                  Search for a product by name, SKU, brand, or spec to add it to this draft.
                </div>
              }
              title="Search products"
              description="Use the same product search, filters, dynamic facets, pricing, and pagination as /products."
            />
          </div>

          <aside className="space-y-3 rounded-lg border bg-muted/20 p-4">
            <div>
              <h3 className="text-sm font-semibold">Selected product</h3>
              <p className="text-xs text-muted-foreground">
                Click a product result, then set the quote quantity.
              </p>
            </div>
            {selectedProduct ? (
              <div className="space-y-3 rounded-lg border bg-background p-3">
                <div className="flex gap-3">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
                    {selectedProduct.thumbnail ? (
                      <img
                        src={selectedProduct.thumbnail}
                        alt={selectedProduct.title}
                        className="h-full w-full object-contain p-1"
                      />
                    ) : (
                      <span className="text-lg font-semibold text-muted-foreground/60">
                        {selectedProduct.model?.charAt(0) || '?'}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 text-sm">
                    <p className="font-medium leading-tight">{selectedProduct.title || selectedProduct.model}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{selectedProduct.model}</p>
                    <p className="mt-1 text-xs text-primary">{selectedProduct.manufacturer}</p>
                    {selectedProduct.boughtThrough ? (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Bought through {selectedProduct.boughtThrough}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="add-product-quantity">Quantity</Label>
                  <InlineFieldInput
                    id="add-product-quantity"
                    type="number"
                    min={1}
                    step={1}
                    value={quantity}
                    onChange={(event) => {
                      const next = Number(event.target.value)
                      setQuantity(Number.isFinite(next) && next > 0 ? Math.floor(next) : 1)
                    }}
                  />
                </div>
                {needsPrice ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-1.5">
                      <Label htmlFor="add-product-net">Supplier net</Label>
                      <InlineFieldInput
                        id="add-product-net"
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={netPrice}
                        onChange={(event) => setNetPrice(event.target.value)}
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor="add-product-sell">Customer sell</Label>
                      <InlineFieldInput
                        id="add-product-sell"
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={sellPrice}
                        onChange={(event) => setSellPrice(event.target.value)}
                      />
                    </div>
                  </div>
                ) : (
                  <p className="text-sm font-medium">{formatMoney(selectedProduct.price)} each</p>
                )}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed bg-background p-4 text-sm text-muted-foreground">
                No product selected yet.
              </div>
            )}
          </aside>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={addLineItem.isPending}>
            Cancel
          </Button>
          <LoadingButton
            loading={addLineItem.isPending}
            loadingText="Adding..."
            disabled={
              !selectedProduct || quantity < 1 || (needsPrice && !priceEntered) || addLineItem.isPending
            }
            onClick={handleAddProduct}
          >
            <Plus className="h-4 w-4" />
            Add selected product
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function LineItemsSection({
  draft,
  onSave,
  disabled,
}: {
  draft: DropshipDraft
  onSave: (fieldKey: string, rawValue: string, kind?: FieldKind) => void
  disabled?: boolean
}) {
  const lines = quoteLineItems(draft)
  const computedLines = reviewLines(draft)
  const deleteLineItem = useDeleteDropshipQuoteDraftLineItem(draft.uuid)
  const linksByIndex = new Map(draft.linked_line_items.map((link) => [link.line_index, link]))
  const canMutateLineItems = !DRAFT_TERMINAL_STATUSES.has(draft.status)
  let lastManufacturer = ''

  const handleConfirmRemove = (removeIndex: number, close: () => void) => {
    deleteLineItem.mutate(
      { lineIndex: removeIndex },
      {
        onSuccess: () => {
          toast.success('Product removed from quote')
          close()
        },
        onError: (error) => toast.error(`Remove failed: ${getErrorMessage(error, 'unknown error')}`),
      },
    )
  }

  const renderRemoveButton = (index: number, className: string) => (
    <ActionPopover
      align="end"
      trigger={
        <LoadingButton
          variant="ghost"
          size="icon"
          className={className}
          aria-label={`Remove line item ${index + 1}`}
          loading={deleteLineItem.isPending && deleteLineItem.variables?.lineIndex === index}
          disabled={disabled || deleteLineItem.isPending}
        >
          <Trash2 className="h-4 w-4" />
        </LoadingButton>
      }
      title="Remove product line item?"
      confirmLabel="Confirm remove"
      confirmVariant="destructive"
      pending={deleteLineItem.isPending}
      pendingLabel="Removing..."
      onConfirm={(_note, close) => handleConfirmRemove(index, close)}
    >
      <p className="text-xs text-muted-foreground">
        This removes line item {index + 1} from the quote draft. The draft stays editable and can be
        updated with Add products again.
      </p>
      {deleteLineItem.error && (
        <div
          role="alert"
          className="flex gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">Line item could not be removed.</p>
            <p>{getErrorMessage(deleteLineItem.error, 'Try again.')}</p>
          </div>
        </div>
      )}
    </ActionPopover>
  )

  const renderFields = (
    line: JsonRecord,
    reviewLine: JsonRecord,
    index: number,
    mobile = false,
  ) => (
    <>
      {EDITABLE_LINE_FIELDS.map((field) => {
        const fieldKey = `quote.line_items.${index}.${field.key}`
        const rawValue = lineFieldValue(line, reviewLine, field.key)
        const displayValue =
          field.kind === 'money' ? moneyText(rawValue) : (friendlyValue(rawValue) ?? '-')
        return mobile ? (
          <div key={field.key} className="flex justify-between gap-3">
            <span className="text-muted-foreground">{field.label}</span>
            <FieldEditor
              label={field.label}
              fieldKey={fieldKey}
              value={rawValue}
              displayValue={displayValue}
              kind={field.kind}
              compact
              align="right"
              showLabel={false}
              disabled={disabled}
              onSave={onSave}
            />
          </div>
        ) : (
          <TableCell key={field.key} className={cn(field.key !== 'discount_chain' && 'text-right')}>
            <FieldEditor
              label={field.label}
              fieldKey={fieldKey}
              value={rawValue}
              displayValue={displayValue}
              kind={field.kind}
              compact
              align={field.key === 'discount_chain' ? 'left' : 'right'}
              showLabel={false}
              disabled={disabled}
              onSave={onSave}
            />
          </TableCell>
        )
      })}
    </>
  )

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 pb-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <CardTitle className="text-base">Line items by manufacturer</CardTitle>
          <p className="text-sm text-muted-foreground">
            Uses the same rich product row and click-to-edit field behavior as PO details line items.
          </p>
          {!canMutateLineItems && (
            <p className="mt-1 text-xs text-muted-foreground">
              Add and remove actions are hidden because this draft is {formatStatus(draft.status)}.
            </p>
          )}
        </div>
        {canMutateLineItems && <AddProductsDialog draft={draft} disabled={disabled} />}
      </CardHeader>
      <CardContent>
        <div className="hidden overflow-x-auto md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[320px]">Product</TableHead>
                <TableHead className="w-28 text-right">Qty</TableHead>
                <TableHead className="w-36 text-right">List</TableHead>
                <TableHead className="w-36 text-right">Discount</TableHead>
                <TableHead className="w-36 text-right">Net</TableHead>
                <TableHead className="w-36 text-right">Sell</TableHead>
                <TableHead className="w-36 text-right">Sell Total</TableHead>
                {canMutateLineItems && <TableHead className="w-12" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.length ? (
                lines.flatMap((line, index) => {
                  const reviewLine = computedLines[index] ?? {}
                  const link = linksByIndex.get(index)
                  const manufacturer =
                    link?.manufacturer_display_name ??
                    friendlyValue(line.manufacturer_name) ??
                    'Manufacturer not parsed'
                  const showGroup = manufacturer !== lastManufacturer
                  lastManufacturer = manufacturer
                  const rows = []
                  if (showGroup) {
                    rows.push(
                      <TableRow key={`group-${index}`} className="bg-muted/50 hover:bg-muted/50">
                        <TableCell
                          colSpan={canMutateLineItems ? 8 : 7}
                          className="whitespace-normal break-words py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                        >
                          {manufacturer}
                        </TableCell>
                      </TableRow>,
                    )
                  }
                  rows.push(
                    <TableRow key={`line-${index}`} className="align-top">
                      <TableCell className="min-w-0 max-w-[560px] whitespace-normal">
                        <ProductIdentity line={line} link={link} />
                      </TableCell>
                      {renderFields(line, reviewLine, index)}
                      <TableCell className="whitespace-nowrap text-right tabular-nums">
                        <p className="font-medium">{moneyText(lineSellTotal(line, reviewLine))}</p>
                      </TableCell>
                      {canMutateLineItems && (
                        <TableCell className="text-right">
                          {renderRemoveButton(index, 'h-8 w-8 text-destructive')}
                        </TableCell>
                      )}
                    </TableRow>,
                  )
                  return rows
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={canMutateLineItems ? 8 : 7}
                    className="py-8 text-center text-muted-foreground"
                  >
                    No quote lines parsed yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="space-y-2 md:hidden">
          {lines.length ? (
            lines.map((line, index) => {
              const reviewLine = computedLines[index] ?? {}
              const link = linksByIndex.get(index)
              const manufacturer =
                link?.manufacturer_display_name ??
                friendlyValue(line.manufacturer_name) ??
                'Manufacturer not parsed'
              return (
                <div key={`mobile-${index}`} className="rounded-lg border bg-card p-3">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <p className="min-w-0 whitespace-normal break-words text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {manufacturer}
                    </p>
                    {canMutateLineItems &&
                      renderRemoveButton(index, '-mt-1 h-8 w-8 shrink-0 text-destructive')}
                  </div>
                  <ProductIdentity line={line} link={link} />
                  <div className="mt-3 grid grid-cols-1 gap-y-1.5 text-xs">
                    {renderFields(line, reviewLine, index, true)}
                    <div className="flex justify-between border-t pt-1.5">
                      <span className="text-muted-foreground">Sell Total</span>
                      <span className="font-semibold">
                        {moneyText(lineSellTotal(line, reviewLine))}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })
          ) : (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No quote lines parsed yet.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function TotalsAndAccessorials({
  draft,
  onSave,
  disabled,
}: {
  draft: DropshipDraft
  onSave: (fieldKey: string, rawValue: string, kind?: FieldKind) => void
  disabled?: boolean
}) {
  const totalValues = draftTotals(draft)
  const totalFields = [
    {
      key: 'subtotal',
      label: 'Subtotal',
      value: totalValues.subtotal,
      editable: false,
    },
    {
      key: 'quote.freight_total',
      label: 'Shipping',
      value: totalValues.shipping,
      editable: true,
    },
    {
      key: 'quote.tax_total',
      label: 'Tax',
      value: totalValues.tax,
      editable: true,
    },
    {
      key: 'quote.total',
      label: 'Total',
      value: totalValues.total,
      editable: false,
    },
  ]
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px]">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Accessorial questions</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-3">
          {ACCESSORIAL_FIELDS.map((field) => (
            <div key={field.key} className="flex items-start justify-between gap-4 rounded-md border bg-background p-3">
              <div className="space-y-0.5">
                <Label htmlFor={field.key} className="text-sm font-medium">
                  {field.label}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {fieldValue(draft, field.key) === true ? 'Required' : 'Not required'}
                </p>
              </div>
              <Switch
                id={field.key}
                checked={fieldValue(draft, field.key) === true}
                disabled={disabled}
                onCheckedChange={(checked) => onSave(field.key, String(checked), field.kind)}
              />
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Quote totals</CardTitle>
          <p className="text-sm text-muted-foreground">
            Total uses the backend computed order total when available.
          </p>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {totalFields.map((field) => (
            <div
              key={field.key}
              className={cn(
                'flex items-center justify-between gap-3',
                field.key === 'quote.total' && 'border-t pt-2',
              )}
            >
              <span
                className={cn(
                  'text-muted-foreground',
                  field.key === 'quote.total' && 'font-semibold text-foreground',
                )}
              >
                {field.label}
              </span>
              {field.editable ? (
                <FieldEditor
                  label={field.label}
                  fieldKey={field.key}
                  value={field.value}
                  displayValue={moneyText(field.value)}
                  kind="money"
                  compact
                  align="right"
                  showLabel={false}
                  disabled={disabled}
                  onSave={onSave}
                />
              ) : (
                <span className="flex flex-col items-end">
                  <span className="whitespace-nowrap text-right text-sm font-medium tabular-nums">
                    {moneyText(field.value)}
                  </span>
                  {field.key === 'quote.total' && totalValues.usesComputedOrderTotal && (
                    <span className="text-[11px] font-normal text-muted-foreground">
                      Backend computed_order_total
                    </span>
                  )}
                </span>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}

function NotesCard({
  draft,
  disabled,
  onSaveNotes,
}: {
  draft: DropshipDraft
  disabled?: boolean
  onSaveNotes: (notes: string, onSuccess: () => void) => void
}) {
  const [editing, setEditing] = useState(false)
  const inputRef = useRef<MentionInputHandle>(null)

  const handleSave = useCallback(() => {
    const result = inputRef.current?.getContent()
    if (!result) return
    onSaveNotes(result.content.trim(), () => setEditing(false))
  }, [onSaveNotes])

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Quote notes</CardTitle>
        <p className="text-sm text-muted-foreground">Operator notes live with the quote draft.</p>
      </CardHeader>
      <CardContent>
        {editing ? (
          <>
            <MentionInput
              ref={inputRef}
              initialValue={draft.draft_notes ?? ''}
              placeholder="Add notes... Use @ to mention entities"
              disabled={disabled}
              className="min-h-[60px] text-sm"
              onSubmit={handleSave}
            />
            <div className="flex gap-1 mt-2">
              <Button size="sm" variant="ghost" onClick={handleSave} disabled={disabled}>
                {disabled ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Check className="h-3 w-3 mr-1" />
                )}
                {disabled ? 'Saving...' : 'Save'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditing(false)}
                disabled={disabled}
              >
                <X className="h-3 w-3 mr-1" />
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <div className="group/notes">
            {draft.draft_notes ? (
              <div className="text-sm">
                <MentionText content={draft.draft_notes} />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">No notes yet</p>
            )}
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="mt-1.5 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors opacity-0 group-hover/notes:opacity-100"
            >
              <Pencil className="h-3 w-3" />
              Edit
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function ValidationIssuesCard({ draft }: { draft: DropshipDraft }) {
  const validationDetails = draftValidationDetails(draft)
  if (!validationDetails.length) return null

  return (
    <Card className="border-amber-200 bg-amber-50/60">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base text-amber-950">
          <AlertCircle className="h-4 w-4" />
          Validation issues
        </CardTitle>
        <p className="text-sm text-amber-900">
          Resolve these review issues before confirming the dropship quote draft.
        </p>
      </CardHeader>
      <CardContent>
        <ul className="list-disc space-y-1 pl-4 text-sm text-amber-950">
          {validationDetails.map((detail) => (
            <li key={detail}>{detail}</li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

function ConfirmQuoteCard({ draft }: { draft: DropshipDraft }) {
  const [approved, setApproved] = useState(false)
  const confirmDraft = useConfirmDropshipQuoteDraft(draft.uuid)
  const project = confirmationProject(draft)
  const projectStarted = draft.status === 'project_started' || !!project.uuid
  const confirmErrorMessage = confirmDraft.error
    ? getDropshipErrorMessage(confirmDraft.error, 'unknown error', draft)
    : null

  const handleConfirm = () => {
    confirmDraft.mutate(
      {
        approval_note: 'Confirmed from Dropship Quote Draft detail UI',
        dry_run: false,
      },
      {
        onSuccess: () => toast.success('Quote draft converted to dropship project'),
        onError: (error) =>
          toast.error(`Confirm failed: ${getDropshipErrorMessage(error, 'unknown error', draft)}`),
      },
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Confirm quote draft</CardTitle>
        <p className="text-sm text-muted-foreground">
          Finalize this reviewed draft and start the dropship project.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {projectStarted ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            <p className="font-medium">Project started</p>
            {project.uuid ? (
              <Link
                to="/projects/$projectId"
                params={{ projectId: project.uuid }}
                className="mt-1 inline-flex text-primary hover:underline"
              >
                Open dropship project
              </Link>
            ) : project.id ? (
              <p className="mt-1 text-xs">Project ID {project.id}</p>
            ) : null}
          </div>
        ) : (
          <>
            {confirmErrorMessage && (
              <div
                role="alert"
                className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
              >
                <p className="font-medium">Confirm failed</p>
                <p className="mt-1 text-xs">{confirmErrorMessage}</p>
              </div>
            )}
            <label className="flex items-start gap-2 rounded-lg border bg-background p-3 text-sm">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-muted-foreground"
                checked={approved}
                onChange={(event) => setApproved(event.target.checked)}
              />
              <span>
                <span className="font-medium">Confirm</span>
                <span className="block text-xs text-muted-foreground">
                  I reviewed pricing, customer charges, and delivery requirements.
                </span>
              </span>
            </label>
            <LoadingButton
              className="w-full"
              loading={confirmDraft.isPending}
              loadingText="Starting project..."
              disabled={!approved || confirmDraft.isPending}
              onClick={handleConfirm}
            >
              <Check className="h-4 w-4" />
              Confirm and start project
            </LoadingButton>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function TimelineCard({ events }: { events?: DropshipDraftEvent[] }) {
  const mapped = useMemo(
    () =>
      (events ?? []).map((event) => ({
        uuid: event.uuid,
        occurred_at: event.created_at,
        event_category: event.event_category || 'system',
        event_type: event.event_type,
        title: event.title,
        description: event.description,
        actor_name: event.actor_name,
        actor_type: event.actor_type,
        metadata_json: null,
      })),
    [events],
  )
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock3 className="h-4 w-4" />
          Timeline
        </CardTitle>
      </CardHeader>
      <CardContent>
        {mapped.length ? (
          <TimelineList events={mapped} />
        ) : (
          <p className="text-sm text-muted-foreground">No timeline entries yet.</p>
        )}
      </CardContent>
    </Card>
  )
}

function DraftProgressSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-36 w-full" />
      <Skeleton className="h-72 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  )
}

function getErrorMessage(error: unknown, fallback: string): string {
  return getDropshipErrorMessage(error, fallback)
}

export function DropshipDraftProgressPage({ draftUuid }: { draftUuid: string }) {
  const draftQuery = useDropshipQuoteDraft(draftUuid)
  const eventsQuery = useDropshipQuoteDraftEvents(draftUuid)
  const patchDraft = usePatchDropshipQuoteDraft(draftUuid)
  const draft = draftQuery.data

  const saveField = (fieldKey: string, rawValue: string, kind?: FieldKind) => {
    const body: DropshipPatch = {
      field_updates: {
        [fieldKey]: {
          value: parseFieldValue(fieldKey, rawValue, kind),
          manual_review: false,
        } as DraftFieldUpdate,
      },
    }
    patchDraft.mutate(body, {
      onSuccess: () => toast.success('Field confirmed'),
      onError: (error) => toast.error(`Update failed: ${getErrorMessage(error, 'unknown error')}`),
    })
  }

  const saveNotes = (notes: string, onSuccess?: () => void) => {
    patchDraft.mutate(
      { field_updates: {}, draft_notes: notes },
      {
        onSuccess: () => {
          toast.success('Quote notes confirmed')
          onSuccess?.()
        },
        onError: (error) =>
          toast.error(`Notes update failed: ${getErrorMessage(error, 'unknown error')}`),
      },
    )
  }

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" size="sm" asChild className="-ml-2">
          <Link to="/projects/dropship-drafts">
            <ArrowLeft className="h-4 w-4" />
            Dropship Quote Drafts
          </Link>
        </Button>
      </div>

      {draftQuery.isLoading ? (
        <DraftProgressSkeleton />
      ) : draftQuery.error ? (
        <Card className="border-destructive/30">
          <CardContent className="flex gap-3 p-4 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4" />
            <div>
              <p className="font-medium">Draft could not be loaded.</p>
              <p>{getErrorMessage(draftQuery.error, 'Try again from the quote draft list.')}</p>
            </div>
          </CardContent>
        </Card>
      ) : draft ? (
        <div key={`${draft.uuid}:${draft.updated_at}`} className="space-y-5">
          <QuoteHeader draft={draft} />
          <QuotePartiesHeader draft={draft} onSave={saveField} disabled={patchDraft.isPending} />
          <LineItemsSection draft={draft} onSave={saveField} disabled={patchDraft.isPending} />
          <TotalsAndAccessorials draft={draft} onSave={saveField} disabled={patchDraft.isPending} />
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_420px]">
            <TimelineCard events={eventsQuery.data} />
            <div className="space-y-4">
              <ValidationIssuesCard draft={draft} />
              <ConfirmQuoteCard draft={draft} />
              <NotesCard draft={draft} disabled={patchDraft.isPending} onSaveNotes={saveNotes} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
